import { useMemo } from 'react';

import Config from '@config';
import { initializeOrganizationsApi } from '@gasbi/shared/index';
import { useSelector } from 'react-redux';

import { cachedItemByIdSelector } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

// BMS's ГРМ card, list and lookups are billing-organizations; the roles are the shared RBAC's, under
// organizations (GSB-8317). BMS has no organization create endpoint any more.
const organizations = initializeOrganizationsApi({
  baseUrl: Config.VITE_API_CLIENT_URL,
  baseQuery: axiosBaseQuery,
  organizationsPath: 'billing-organizations',
});

export const {
  organizationsApi,
  useGetReferenceBooksQuery,
  useGetOrganizationsGdnQuery,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationByIdMutation,
  useGetOrganizationClassifiersQuery,
  useGetOrganizationDepartmentsQuery,
  useGetOrganizationEmployeePositionsQuery,
  useAddRepresentativeToOrganizationMutation,
  useRemoveRepresentativeFromOrganizationMutation,

  useGetOrganizationsInfiniteQuery,
  useGetOrganizationRolesInfiniteQuery,
} = organizations;

const getCachedOrganizationByIdSelector = cachedItemByIdSelector(
  'organizationsApi',
  'getOrganizations',
);

export const useGetCachedOrganizationByIdSelector = id =>
  useSelector(getCachedOrganizationByIdSelector(id));

const getCachedRoleByIdSelector = cachedItemByIdSelector(
  'organizationsApi',
  'getOrganizationRoles',
);

export const useGetCachedRoleByIdSelector = id =>
  useSelector(getCachedRoleByIdSelector(id));

/** The roles filter of the shared employees list (RbacOrganization), filtered by name in memory as in Supply. */
export const useGetRolesForFilterQuery = (
  { organizationId, name }: { organizationId?: string; name?: string } = {},
  options?: { skip?: boolean },
) => {
  const result = useGetOrganizationRolesInfiniteQuery(
    organizationId ? { organizationId } : undefined,
    options,
  );

  const data = useMemo(() => {
    const roles = result.data ?? [];
    if (!name) return roles;
    const term = name.toLowerCase();
    return roles.filter(role => role.name.toLowerCase().includes(term));
  }, [result.data, name]);

  return { ...result, data };
};
